import { MouseEvent, useCallback } from 'react'
import Divider from '@mui/material/Divider'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import cn from 'classnames'
import Icon from '../Common/Icon'
import Text from '../Common/Text'
import styles from './ContextSwitcherItems.module.scss'

export interface IContextOption {
  value: string
  label: string
}

interface IProps {
  title: string
  options: IContextOption[]
  currentValue?: string
  itemClassName?: string
  onSelect: (value: string) => void
}

/**
 * Menu rows for picking which of your companies the portal is scoped to.
 * Renders nothing with fewer than two to choose from.
 */
const ContextSwitcherItems = ({
  title,
  options,
  currentValue,
  itemClassName,
  onSelect,
}: IProps) => {
  const handleClick = useCallback(
    (event: MouseEvent<HTMLLIElement>) => {
      const { value } = event.currentTarget.dataset
      if (value && value !== currentValue) {
        onSelect(value)
      }
    },
    [currentValue, onSelect],
  )

  if (options.length < 2) {
    return null
  }

  // Direct children of the menu list, not a wrapper: MenuList's arrow-key traversal only walks
  // its own children, so rows nested in a div are unreachable from the keyboard.
  return [
    <ListSubheader key="title" disableSticky className={styles.title} data-cy="context-switcher">
      {title}
    </ListSubheader>,
    ...options.map(({ value, label }) => {
      const isCurrent = value === currentValue
      return (
        <MenuItem
          key={value}
          data-value={value}
          selected={isCurrent}
          classes={{ root: cn(itemClassName, styles.item) }}
          onClick={handleClick}
        >
          <Text variant="menuItem" casing="none" className={styles.label}>
            {label}
          </Text>
          {isCurrent && <Icon name="check" size="xs" className={styles.check} />}
        </MenuItem>
      )
    }),
    <Divider key="divider" className={styles.divider} />,
  ]
}

// MenuList hands initial focus to its first child, and this element can't take it (it renders
// several rows, or none), so the menu skips it and focuses the next item.
ContextSwitcherItems.muiSkipListHighlight = true

export default ContextSwitcherItems
